/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FormValidators } from 'src/core/utils/formValidators';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';

export type RegexEntityDetailsProps = {
    /**
     * The entity that is being displayed.
     */
    entityStore: RegexEntityStore;

    editingAllowed: boolean;
};

const messages = defineMessages({
    directions: {
        id: 'entityDetails.regexEntityDetails.directions',
        defaultMessage:
            'List entities represent a fixed, closed set of related words along with their synonyms. List entities are extracted by an exact text match and can be resolved to a normalized value.'
    },
    link: {
        id: 'entityDetails.regexEntityDetails.link',
        defaultMessage: 'Learn more about Regex entities.'
    },
    patternSectionTitle: {
        id: 'entityDetails.regexEntityDetails.patternSectionTitle',
        defaultMessage: 'Regex'
    },
    patternFieldPlaceholder: {
        id: 'entityDetails.regexEntityDetails.patternFieldPlaceholder',
        defaultMessage: 'Type regex value ...'
    },
    updateEntityToastStart: {
        id: 'entityDetails.regexEntityDetails.updateEntityToastStart',
        defaultMessage: 'Updating entity ...'
    },
    updateEntityToastSuccess: {
        id: 'entityDetails.regexEntityDetails.updateEntityToastSuccess',
        defaultMessage: 'Update entity completed'
    },
    updateEntityToastFailed: {
        id: 'entityDetails.regexEntityDetails.updateEntityToastFailed',
        defaultMessage: 'Update entity failed'
    }
});

/**
 * Represents the details of a regex entity, which includes
 * controls for displaying and changing the entity regex pattern.
 *
 * @param props The input properties for the section.
 */
export const RegexEntityDetails = (props: RegexEntityDetailsProps) => {
    const {
        entityStore: { name, pattern, id: entityId },
        editingAllowed
    } = props;
    const { localize } = useLocalization();
    const { formState, updateFormState, formStateDto } = useFormState<Pick<RegexEntityStore, 'pattern'>>({
        pattern: [pattern, FormValidators.createRequiredValidator()]
    });
    const { dispatcher } = useGlobalState();

    /**
     * Submits the pattern entity
     */
    const submitPattern = (): void => {
        if (pattern === formStateDto.pattern) {
            return;
        }

        if (formState.form.valid) {
            dispatcher.dispatch('updateRegexEntity', {
                name,
                pattern: formStateDto.pattern,
                id: entityId,
                toastData: {
                    running: { title: localize(messages.updateEntityToastStart) },
                    completed: { title: localize(messages.updateEntityToastSuccess) },
                    failed: { title: localize(messages.updateEntityToastFailed) }
                }
            });
        }
    };

    return (
        <LuisTextField
            label={localize(messages.patternSectionTitle)}
            placeholder={localize(messages.patternFieldPlaceholder)}
            value={formState.fields.pattern.value}
            errorMessage={formState.fields.pattern.errorMessage}
            onEnterKeyUp={submitPattern}
            onBlur={submitPattern}
            onChange={(_, newPattern) => updateFormState({ pattern: newPattern })}
            disabled={!editingAllowed}
            required
        />
    );
};
